import React, { useEffect, useRef, useState } from "react";
import {
  authOptions, changeEmail, changePassword, forgotPassword, resendVerification,
  resetPassword, signIn, signInUrl, signUp, type ApiResult, type AuthOptions,
} from "../lib/api";
import { type Banner } from "../lib/authui";

/**
 * Sign in, sign up, and recover — beside the Google button, never in front of it.
 *
 * NOTHING HERE GATES THE WALLET. Invariant 13: an account adds record sync, a
 * team view and an audit trail, and is never in the path of a transaction. This
 * panel is opened deliberately from the top bar and closes again; it does not
 * mount over the console, and a person who never opens it loses nothing.
 *
 * The signup form is hidden entirely when the deployment cannot send mail,
 * because a confirmation link that cannot be sent is an account nobody can use.
 */

type Mode = "signin" | "signup" | "forgot" | "reset";

/**
 * `resetToken` is passed in, not read from the URL: AccountBar reads the URL
 * once and erases it immediately, so by the time this mounts there is nothing
 * left to read. One owner for one piece of global state.
 */
export function AuthPanel(props: { onDone: () => void; resetToken?: string | null }) {
  const [opts, setOpts] = useState<AuthOptions | null>(null);
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<Banner>(null);
  const [resetToken, setResetToken] = useState<string | null>(null);
  const turnstileBox = useRef<HTMLDivElement | null>(null);
  const turnstileToken = useRef<string>("");

  useEffect(() => {
    void authOptions().then(setOpts);
    if (props.resetToken) {
      setResetToken(props.resetToken);
      setMode("reset");
    }
  }, [props.resetToken]);

  // Turnstile, only when configured and only on the form that needs it.
  useEffect(() => {
    if (mode !== "signup" || !opts?.turnstileSiteKey || !turnstileBox.current) return;
    const w = window as unknown as { turnstile?: { render(el: Element, o: unknown): void } };
    const mount = () => {
      if (!turnstileBox.current) return;
      w.turnstile?.render(turnstileBox.current, {
        sitekey: opts.turnstileSiteKey,
        callback: (t: string) => (turnstileToken.current = t),
        "error-callback": () => (turnstileToken.current = ""),
        "expired-callback": () => (turnstileToken.current = ""),
      });
    };
    if (w.turnstile) return mount();
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.onload = mount;
    document.head.appendChild(s);
  }, [mode, opts?.turnstileSiteKey]);

  function show<T>(r: ApiResult<T>, okText: string) {
    if (r.ok) setNote({ kind: "ok", text: okText });
    else setNote({ kind: "danger", text: r.detail });
    return r.ok;
  }

  async function run(fn: () => Promise<void>) {
    if (busy) return;
    setBusy(true);
    setNote(null);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  }

  const submit = () =>
    run(async () => {
      if (mode === "signin") {
        const r = await signIn(email, password);
        if (show(r, "Signed in.")) props.onDone();
        return;
      }
      if (mode === "signup") {
        const r = await signUp(email, password, turnstileToken.current || undefined);
        if (r.ok) setNote({ kind: "ok", text: r.data.detail });
        else setNote({ kind: "danger", text: r.detail });
        return;
      }
      if (mode === "forgot") {
        const r = await forgotPassword(email);
        // Deliberately the server's own words: they are identical whether or
        // not the address has an account, and paraphrasing risks leaking that.
        setNote(r.ok ? { kind: "ok", text: r.data.detail } : { kind: "danger", text: r.detail });
        return;
      }
      if (mode === "reset" && resetToken) {
        const r = await resetPassword(resetToken, password);
        if (show(r, "Password changed. All other sessions have been signed out.")) props.onDone();
      }
    });

  if (!opts) return <div className="auth-panel" data-testid="auth-panel">Loading…</div>;

  const title =
    mode === "signup" ? "Create an account"
    : mode === "forgot" ? "Reset your password"
    : mode === "reset" ? "Choose a new password"
    : "Sign in";

  return (
    <div className="auth-panel" data-testid="auth-panel">
      <div className="auth-head">
        <strong data-testid="auth-title">{title}</strong>
        <button className="ghost" onClick={props.onDone} data-testid="auth-close">Close</button>
      </div>

      {note ? (
        <p className={`auth-note ${note.kind}`} data-testid="auth-note">{note.text}</p>
      ) : null}

      {mode !== "reset" ? (
        <>
          {opts.providers.map((p) => (
            <a
              key={p.id}
              className="btnlink auth-oauth"
              href={signInUrl(p.id, window.location.pathname)}
              data-testid={`btn-signin-${p.id}`}
            >
              Continue with {p.label}
            </a>
          ))}
          {opts.providers.length > 0 && opts.password ? <div className="auth-or"><span>or</span></div> : null}
        </>
      ) : null}

      {opts.password || mode === "reset" ? (
        <form
          className="auth-form"
          onSubmit={(e) => { e.preventDefault(); void submit(); }}
        >
          {mode !== "reset" ? (
            <label>
              Email
              <input
                type="email"
                autoComplete="email"
                data-testid="auth-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
          ) : null}

          {mode !== "forgot" ? (
            <label>
              {mode === "signin" ? "Password" : "New password"}
              <input
                type="password"
                autoComplete={mode === "signin" ? "current-password" : "new-password"}
                data-testid="auth-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={mode === "signin" ? undefined : 12}
                required
              />
            </label>
          ) : null}

          {mode === "signup" && !password ? (
            <p className="auth-hint">At least 12 characters. Length beats symbols; no symbol is required.</p>
          ) : null}

          {mode === "signup" && opts.turnstileSiteKey ? (
            <div ref={turnstileBox} data-testid="auth-turnstile" />
          ) : null}

          <button type="submit" disabled={busy} data-testid="auth-submit">
            {busy ? "Working…"
              : mode === "signin" ? "Sign in"
              : mode === "signup" ? "Create account"
              : mode === "forgot" ? "Email me a link"
              : "Set new password"}
          </button>
        </form>
      ) : (
        <p className="auth-hint" data-testid="auth-no-password">
          {opts.providers.length > 0
            ? "Email sign-up is not available here — use one of the buttons above."
            : "Accounts are not available on this deployment yet. Everything else works without one."}
        </p>
      )}

      {mode !== "reset" && opts.password ? (
        <div className="auth-switch">
          {mode !== "signin" ? <button className="ghost" data-testid="to-signin" onClick={() => setMode("signin")}>Sign in</button> : null}
          {mode !== "signup" ? <button className="ghost" data-testid="to-signup" onClick={() => setMode("signup")}>Create an account</button> : null}
          {mode !== "forgot" ? <button className="ghost" data-testid="to-forgot" onClick={() => setMode("forgot")}>Forgot password</button> : null}
          {mode === "signin" ? (
            <button
              className="ghost"
              data-testid="to-resend"
              onClick={() => void run(async () => {
                const r = await resendVerification(email);
                setNote(r.ok ? { kind: "ok", text: r.data.detail } : { kind: "danger", text: r.detail });
              })}
            >
              Resend confirmation
            </button>
          ) : null}
        </div>
      ) : null}

      <p className="auth-hint">
        An account stores your rehearsals and an audit trail. It never touches your wallet,
        never holds a key, and is not required to launch anything. oxMinter will never ask
        for a password or seed phrase by email.
      </p>
    </div>
  );
}

/** Credential settings for a signed-in user. Rendered from the account bar. */
export function AuthSettings(props: { email: string | null; hasPassword: boolean }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<Banner>(null);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [newEmail, setNewEmail] = useState("");

  async function run(fn: () => Promise<ApiResult<{ detail: string }>>) {
    if (busy) return;
    setBusy(true);
    setNote(null);
    const r = await fn();
    setNote(r.ok ? { kind: "ok", text: r.data.detail } : { kind: "danger", text: r.detail });
    setBusy(false);
  }

  if (!props.hasPassword) {
    return (
      <p className="auth-hint" data-testid="settings-oauth-only">
        This account signs in with an identity provider, so there is no password here to change.
      </p>
    );
  }

  return (
    <div className="auth-settings" data-testid="auth-settings">
      <form onSubmit={(e) => { e.preventDefault(); void run(() => changePassword(current, next)); }}>
        <strong>Change password</strong>
        <input type="password" autoComplete="current-password" placeholder="Current password"
               data-testid="settings-current" value={current} onChange={(e) => setCurrent(e.target.value)} required />
        <input type="password" autoComplete="new-password" placeholder="New password" minLength={12}
               data-testid="settings-next" value={next} onChange={(e) => setNext(e.target.value)} required />
        <button type="submit" disabled={busy} data-testid="settings-change-password">Change password</button>
        <p className="auth-hint">Every other session is signed out when the password changes.</p>
      </form>

      <form onSubmit={(e) => { e.preventDefault(); void run(() => changeEmail(newEmail, current)); }}>
        <strong>Change email</strong>
        <input type="email" autoComplete="email" placeholder="New address"
               data-testid="settings-email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required />
        <input type="password" autoComplete="current-password" placeholder="Current password"
               data-testid="settings-email-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
        <button type="submit" disabled={busy} data-testid="settings-change-email">Send confirmation</button>
        <p className="auth-hint">
          Currently {props.email ?? "not set"}. Nothing changes until the link in the new
          inbox is clicked.
        </p>
      </form>

      {note ? <p className={`auth-note ${note.kind}`} data-testid="settings-note">{note.text}</p> : null}
    </div>
  );
}
